import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive, ArrowDownRight, EyeOff, ArrowUpRight, AtSign, BellOff, Bot, CheckCircle2, Clapperboard, FolderKanban, Gauge, History, Hourglass, Inbox,
  KanbanSquare, MessageSquare, Minus, PenLine, PlayCircle, Sparkles, UserCheck, Users,
  type LucideIcon,
} from 'lucide-react';
import { compareByCodePoint } from '@vd/types';
import { api, type DashboardTicket } from '../../lib/api';
import { useShortcutMap } from '../../lib/keyboard';
import { Duration, When } from '../../lib/When';
import { useAuth } from '../auth/AuthProvider';
import { agentRows } from './agentRows';
import { TypeMarker } from '../ticketTypes/TypeMarker';
import { Card, toneStyle, type Tone } from './DashboardCard';
import { DashboardPrefsProvider, useDashboardPrefs } from './dashboardPrefs';
import { dashboardSummary } from './summary';
import { Sparkline } from './Sparkline';
import { ArrivalsProvider, useArrived } from './arrivals';
import { EmptyState } from '../reports/EmptyState';
import styles from './DashboardSections.module.css';
import { ProjectMark } from '../projects/ProjectMark';

/**
 * The Dashboard's sections (TOADO-720, laid out on Jason's review of 16 Oct)
 * for the workspace the person is in. The main column is what is for you:
 * `lead` (What's new and the welcome panel, from the page), Needs you, Pick up
 * where you left off beside Ready to review, and Gone quiet. The panel on the
 * right holds Your numbers, and under it Agent activity down to the panel's
 * foot (t-x2GtEg: Your team went to Reports' Team page to give it the room),
 * and stays in view while the main column scrolls; on a narrow page it
 * follows the main column. Each card hides when it has nothing to show, and a card
 * beside it widens into the space. Each card reads its own small endpoint,
 * and every one is read again when a live event says something changed.
 *
 * Tickets show as blocks: a picture half as big again as the board table's,
 * the title, the project, the type, who logged it, its comments and any AI
 * work so far. j and k move between them; Enter opens one.
 */
export function DashboardSections({ companyId, lead }: { companyId: string | null; lead?: ReactNode }) {
  const { user } = useAuth();
  return (
    <DashboardPrefsProvider key={user?.id ?? 'nobody'} userId={user?.id ?? 'nobody'}>
      <ArrivalsProvider key={companyId ?? 'none'}>
        <Sections companyId={companyId} lead={lead} />
      </ArrivalsProvider>
    </DashboardPrefsProvider>
  );
}

function Sections({ companyId, lead }: { companyId: string | null; lead?: ReactNode }) {
  const frame = useRef<HTMLDivElement>(null);
  useDashboardLive(companyId);
  useBlockKeys(frame);
  return (
    <div className={styles.frame} ref={frame}>
      <div className={styles.layout}>
        <div className={styles.main} data-testid="dash-main">
          {companyId && <Summary companyId={companyId} />}
          {lead}
          {companyId && <>
            <div className={styles.cardRow}><NeedsYou companyId={companyId} /></div>
            <div className={styles.cardRow}>
              <PickUp companyId={companyId} />
              <ReadyToReview companyId={companyId} />
            </div>
            <div className={styles.cardRow}><GoneQuiet companyId={companyId} /></div>
          </>}
        </div>
        {companyId && (
          <aside className={styles.panel} aria-label="Your numbers and agents" data-testid="dash-panel">
            <YourNumbers companyId={companyId} />
            <HiddenCards />
            <AgentActivity companyId={companyId} />
          </aside>
        )}
      </div>
    </div>
  );
}

/** The page in a sentence or two, from the answers the cards read (`summary.ts`). */
function Summary({ companyId }: { companyId: string }) {
  const { data: counts } = useQuery({ queryKey: ['inbox-counts', companyId], queryFn: () => api.getInboxCounts(companyId) });
  const { data: needs } = useQuery({ queryKey: ['dashboard', companyId, 'needs-you'], queryFn: () => api.dashboard.needsYou(companyId) });
  const { data: review } = useQuery({ queryKey: ['dashboard', companyId, 'review'], queryFn: () => api.dashboard.review(companyId) });
  const said = dashboardSummary({
    counts,
    waitingOnYou: needs?.waitingOnYou.length,
    review: review && { walkthroughs: review.walkthroughs.length, intake: review.intake.reduce((sum, project) => sum + project.waiting, 0) },
  });
  return said ? <p className={styles.summary} data-testid="dash-summary">{said}</p> : null;
}

/** What a live event can change on the Dashboard: every card is read again, a moment after the last event. */
function useDashboardLive(companyId: string | null) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!companyId) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = api.subscribe(() => {
      // A burst of events (a bulk move) refreshes once.
      clearTimeout(timer);
      timer = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['dashboard', companyId] });
        void queryClient.invalidateQueries({ queryKey: ['inbox-counts', companyId] });
      }, 400);
    });
    return () => { clearTimeout(timer); unsubscribe(); };
  }, [companyId, queryClient]);
}

/** j and k move focus between the Dashboard's ticket and walkthrough blocks, in page order. */
function useBlockKeys(frame: RefObject<HTMLDivElement | null>) {
  const move = (step: number) => (event: KeyboardEvent) => {
    const blocks = [...(frame.current?.querySelectorAll<HTMLElement>('[data-dash-block]') ?? [])];
    if (blocks.length === 0) return;
    event.preventDefault();
    const at = blocks.indexOf(document.activeElement as HTMLElement);
    const next = at === -1 ? (step > 0 ? 0 : blocks.length - 1) : Math.min(blocks.length - 1, Math.max(0, at + step));
    blocks[next]!.focus();
    blocks[next]!.scrollIntoView({ block: 'nearest' });
  };
  useShortcutMap({ j: move(1), k: move(-1) });
}

/** A ticket's picture, or a quiet placeholder in its project's colour when it has none. */
function Thumb({ url, color }: { url: string | null; color: string }) {
  return url
    ? <img className={styles.thumb} src={url} alt="" loading="lazy" />
    : <span className={styles.thumb} style={{ background: `color-mix(in srgb, ${color} 18%, var(--bg-muted))` }} aria-hidden="true" />;
}

/** What AI has done on a ticket so far, in a word or two. */
function aiLabel(ai: DashboardTicket['ai']): string | null {
  if (ai.fixes > 0) return ai.fixes === 1 ? 'AI fix recorded' : `${ai.fixes} AI fixes`;
  return ai.comments ? 'AI commented' : null;
}

/**
 * One ticket as a block (16 Oct): picture, title, then its project, type,
 * who logged it, comments and AI work. `note` is the card's own line (how
 * long it waited, when it was opened, what an agent did); `actions` are
 * buttons laid over its corner, shown on hover or focus.
 */
function TicketBlock({ ticket, note, showProject = true, actions }: { ticket: DashboardTicket; note?: ReactNode; showProject?: boolean; actions?: ReactNode }) {
  const ai = aiLabel(ticket.ai);
  // Brought in by a live event while the page was open: marked for a moment.
  const arrived = useArrived(ticket.id);
  const block = (
    <Link to={`/tickets/${ticket.id}`} className={styles.block} data-testid="dash-ticket-block" data-dash-block data-arrived={arrived || undefined}>
      <Thumb url={ticket.thumbnailUrl} color={ticket.project.color} />
      <span className={styles.blockBody}>
        <span className={styles.blockTitle}>{ticket.title}</span>
        <span className={styles.blockMeta}>
          {showProject && (
            <span className={styles.metaItem}><ProjectMark project={ticket.project} size={14} />{ticket.project.name}</span>
          )}
          {ticket.type && (
            <span className={styles.metaItem}><TypeMarker glyph={ticket.type.glyph} height={14} />{ticket.type.name}</span>
          )}
          <span className={styles.metaItem}>{ticket.loggedBy.visitor ? `Visitor: ${ticket.loggedBy.name}` : ticket.loggedBy.name}</span>
          <span className={styles.metaItem} aria-label={`${ticket.commentCount} comments`}><MessageSquare size={12} aria-hidden="true" />{ticket.commentCount}</span>
          {ai && <span className={`${styles.metaItem} ${styles.aiChip}`}><Sparkles size={11} aria-hidden="true" />{ai}</span>}
        </span>
        {note && <span className={styles.blockNote}>{note}</span>}
      </span>
    </Link>
  );
  if (!actions) return block;
  return <div className={styles.blockWrap}>{block}<div className={styles.blockActions}>{actions}</div></div>;
}

/** A small button over a block's corner. */
function BlockAction({ label, icon: Icon, onClick, busy }: { label: string; icon: LucideIcon; onClick: () => void; busy?: boolean }) {
  return (
    <button type="button" className={styles.blockAction} onClick={onClick} disabled={busy} title={label} aria-label={label}>
      <Icon size={13} aria-hidden="true" /><span>{label}</span>
    </button>
  );
}

/** Runs a ticket action, then reads the Dashboard again; the error, if any, in words. */
function useTicketAction(companyId: string) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (ticketId: string, action: () => Promise<unknown>) => {
    setBusy(ticketId);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: ['dashboard', companyId] });
    } catch (failure) {
      setError((failure as Error).message || 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  return { busy, error, run };
}


/**
 * Needs you, as blocks (16 Oct): what is assigned to you, oldest first, and
 * your tickets waiting on your reply, longest waiting first. With nothing,
 * it says so and offers the next step.
 */
function NeedsYou({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'needs-you'], queryFn: () => api.dashboard.needsYou(companyId) });
  if (!data) return null;
  if (data.assigned.length === 0 && data.waitingOnYou.length === 0) return <AllClear />;
  const column = (ticket: DashboardTicket) => (ticket.column ? ` · ${ticket.column.name}` : '');
  return (
    <Card title="Needs you" testId="dash-needs-you" icon={Inbox} tone="red" size="full" emphasis more={<Link to="/inbox/assigned" className={styles.more}>See all</Link>}>
      {data.assigned.length > 0 && <>
        <h4 className={styles.subTitle}>Assigned to you</h4>
        <div className={styles.blocks}>
          {data.assigned.map((ticket) => <TicketBlock key={ticket.id} ticket={ticket} note={<><Duration since={ticket.since} prefix="Waiting " />{column(ticket)}</>} />)}
        </div>
      </>}
      {data.waitingOnYou.length > 0 && <>
        <h4 className={styles.subTitle}>Waiting on your reply</h4>
        <div className={styles.blocks}>
          {data.waitingOnYou.map((ticket) => <TicketBlock key={ticket.id} ticket={ticket} note={<><When at={ticket.since} prefix="Since " />{column(ticket)}</>} />)}
        </div>
        {/* Answering alone hands nothing back (TOADO-970): nothing moves the ticket or tells the agent. */}
        <p className={styles.note}>Answer on the ticket, then move it on or ask the agent to pick it up again.</p>
      </>}
    </Card>
  );
}

/** Nothing needs you: said plainly, with somewhere to go next. */
function AllClear() {
  return (
    <Card title="All clear" testId="dash-all-clear" icon={CheckCircle2} tone="green" size="full">
      <p className={styles.note}>Nothing is assigned to you and nothing is waiting on your reply.</p>
      <div className={styles.nextSteps}>
        <Link to="/recordings" className={styles.nextStep}><Clapperboard size={14} aria-hidden="true" />Record a walkthrough</Link>
        <Link to="/projects" className={styles.nextStep}><FolderKanban size={14} aria-hidden="true" />Look over the boards</Link>
        <Link to="/inbox/team" className={styles.nextStep}><Users size={14} aria-hidden="true" />See what the team is doing</Link>
      </div>
    </Card>
  );
}

function PickUp({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'recent-tickets'], queryFn: () => api.dashboard.recentTickets(companyId) });
  if (!data || data.tickets.length === 0) return null;
  return (
    <Card title="Pick up where you left off" testId="dash-pick-up" icon={History} tone="blue">
      <div className={styles.blocks}>
        {data.tickets.map((ticket) => (
          <TicketBlock key={ticket.id} ticket={ticket} note={<><When at={ticket.openedAt} prefix="Opened " />{ticket.column ? ` · ${ticket.column.name}` : ''}</>} />
        ))}
      </div>
    </Card>
  );
}

/** An agent's colour, the same one wherever it appears, from its name. */
const AGENT_TONES = ['var(--chart-violet)', 'var(--chart-blue)', 'var(--chart-cyan)', 'var(--chart-magenta)', 'var(--chart-green)', 'var(--chart-amber)'];
function agentTone(agent: string): string {
  let hash = 0;
  for (const char of agent) hash = (hash * 31 + char.codePointAt(0)!) >>> 0;
  return AGENT_TONES[hash % AGENT_TONES.length]!;
}

type AgentLine = { agent: string; did: string; ticket: DashboardTicket; at: string };

/** Lines by ticket, in the order each ticket first appears (newest first, as the answer is). */
function byTicket(lines: AgentLine[]): Array<{ ticket: DashboardTicket; lines: AgentLine[] }> {
  const groups = new Map<string, { ticket: DashboardTicket; lines: AgentLine[] }>();
  for (const line of lines) {
    const group = groups.get(line.ticket.id) ?? { ticket: line.ticket, lines: [] };
    group.lines.push(line);
    groups.set(line.ticket.id, group);
  }
  return [...groups.values()];
}

/** One ticket's agent work: the ticket's block, then each step on a rule, newest first. */
function AgentTimeline({ lines }: { lines: AgentLine[] }) {
  return (
    <div className={styles.blocks}>
      {byTicket(lines).map(({ ticket, lines: steps }) => (
        <div key={ticket.id} className={styles.timelineGroup} data-testid="dash-agent-ticket">
          <TicketBlock ticket={ticket} note={<>{steps.length === 1 ? '1 step' : `${steps.length} steps`} by agents · <When at={steps[0]!.at} prefix="latest " /></>} />
          {/* One row per agent, named once (TOADO-951); its steps fold under it. */}
          <ol className={styles.timeline}>
            {agentRows(steps).map((row) => (
              <li key={row.agent} className={styles.timelineStep} style={{ '--agent': agentTone(row.agent) } as CSSProperties} data-testid="dash-agent-row">
                <details className={styles.agentRow}>
                  <summary className={styles.agentSummary}>
                    <span className={styles.agentBadge} title={row.agent} data-testid="dash-agent-name">
                      {row.tool
                        ? <span className={styles.agentTool} data-testid="dash-agent-tool">{row.tool}</span>
                        : <Bot size={11} aria-label="AI agent" data-testid="dash-agent-bot" />}
                      {row.short}
                    </span>
                    <span className={styles.timelineDid}>{row.summary}</span>
                    <When className={styles.timelineAt} at={row.latest} />
                  </summary>
                  <ol className={styles.agentSteps}>
                    {row.steps.map((step, index) => (
                      <li key={`${step.at}-${index}`} data-testid="dash-agent-step">
                        <span className={styles.timelineDid}>{step.did} it</span>
                        <When className={styles.timelineAt} at={step.at} />
                      </li>
                    ))}
                  </ol>
                </details>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

/**
 * Agent activity over at least the last week (16 Oct): what is new since
 * your last visit first, under its own heading, then what you have seen,
 * each as a timeline by ticket: a ticket's block once, its agents' steps
 * under it. The answer marks it all seen, up to its read time, once shown.
 */
function AgentActivity({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'agent-activity'], queryFn: () => api.dashboard.agentActivity(companyId) });
  const marked = useRef<string | null>(null);
  useEffect(() => {
    if (!data || marked.current === data.readAt) return;
    marked.current = data.readAt;
    void api.dashboard.markSeen(companyId, data.readAt).catch(() => undefined);
  }, [data, companyId]);
  // Where new ended when this card first loaded: a refresh after marking it seen keeps the line where it was.
  const firstSince = useRef<string | null>(null);
  if (!data) return null;
  firstSince.current ??= data.since;
  const since = firstSince.current < data.since ? firstSince.current : data.since;
  const fresh = data.lines.filter((line) => line.at > since);
  const earlier = data.lines.filter((line) => line.at <= since);
  return (
    <Card title="Agent activity" testId="dash-agent-activity" icon={Bot} tone="magenta">
      <div className={styles.agentLines}>
        {data.lines.length === 0 ? (
          <EmptyState art="agents">
            <p>
              No agent activity in the last week.
              {!data.hasAgent && <> <Link to="/me/settings/mcp-setup">Connect an agent</Link>.</>}
            </p>
          </EmptyState>
        ) : <>
          <h4 className={styles.newHeading} data-testid="dash-agent-new">
            {fresh.length > 0 ? `New since your last visit (${fresh.length})` : 'Nothing new since your last visit'}
          </h4>
          {fresh.length > 0 && <AgentTimeline lines={fresh} />}
          {earlier.length > 0 && <>
            <h4 className={styles.subTitle} data-testid="dash-agent-earlier">Earlier this week</h4>
            <AgentTimeline lines={earlier} />
          </>}
        </>}
      </div>
    </Card>
  );
}

function ReadyToReview({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'review'], queryFn: () => api.dashboard.review(companyId) });
  const { busy, error, run } = useTicketAction(companyId);
  if (!data || (data.walkthroughs.length === 0 && data.intakeTickets.length === 0)) return null;
  const waiting = data.intake.reduce((sum, project) => sum + project.waiting, 0);
  /** Out of intake into the board's first column, as the board's own Accept does. */
  const toBoard = (ticket: DashboardTicket) => run(ticket.id, async () => {
    const columns = await api.listColumns(ticket.project.id);
    // The board's own order: by `order`, then by id code point (columnOrder.ts).
    const first = [...columns].sort((x, y) => x.order - y.order || compareByCodePoint(x.id, y.id)).find((column) => !column.isIntake);
    if (!first) throw new Error(`${ticket.project.name} has no column outside intake.`);
    await api.moveTicket(ticket.id, { columnId: first.id });
  });
  return (
    <Card title="Ready to review" testId="dash-review" icon={PlayCircle} tone="green">
      {data.walkthroughs.length > 0 && <>
        <h4 className={styles.subTitle}>Your walkthroughs</h4>
        <div className={styles.blocks}>
          {data.walkthroughs.map((walkthrough) => (
            <Link key={walkthrough.id} to={`/recordings/${walkthrough.id}/review`} className={styles.block} data-testid="dash-walkthrough-block" data-dash-block>
              <Thumb url={walkthrough.thumbnailUrl} color={walkthrough.projectColor} />
              <span className={styles.blockBody}>
                <span className={styles.blockTitle}>Walkthrough in {walkthrough.projectName}</span>
                <span className={styles.blockMeta}>
                  <span className={styles.metaItem}><ProjectMark project={{ id: walkthrough.projectId, name: walkthrough.projectName, color: walkthrough.projectColor }} size={14} />{walkthrough.projectName}</span>
                  <span className={styles.metaItem}>{walkthrough.items === 1 ? '1 item to file' : `${walkthrough.items} items to file`}</span>
                </span>
                <span className={styles.blockNote}>{walkthrough.endedAt ? <><When at={walkthrough.endedAt} prefix="Ended " />{' · '}</> : ''}Review</span>
              </span>
            </Link>
          ))}
        </div>
      </>}
      {data.intakeTickets.length > 0 && <>
        <h4 className={styles.subTitle}>Waiting in intake{waiting > data.intakeTickets.length ? ` (${data.intakeTickets.length} of ${waiting})` : ''}</h4>
        {error && <p role="alert" className={styles.actionError}>{error}</p>}
        <div className={styles.blocks}>
          {data.intakeTickets.map((ticket) => (
            <TicketBlock key={ticket.id} ticket={ticket} note={<When at={ticket.since} prefix="Reported " />}
              actions={<BlockAction label="Move to board" icon={KanbanSquare} busy={busy === ticket.id} onClick={() => void toBoard(ticket)} />} />
          ))}
        </div>
      </>}
    </Card>
  );
}

/** A figure's change against the week before, as an arrow and a count. */
function Trend({ thisWeek, lastWeek }: { thisWeek: number; lastWeek: number }) {
  const Icon = thisWeek > lastWeek ? ArrowUpRight : thisWeek < lastWeek ? ArrowDownRight : Minus;
  return (
    <span className={styles.trend} title={`${thisWeek} in the last 7 days, ${lastWeek} in the 7 before`} data-testid="dash-trend">
      <Icon size={11} aria-hidden="true" />{thisWeek} this week
    </span>
  );
}

function YourNumbers({ companyId }: { companyId: string }) {
  // The same numbers as the sidebar's, from the same answer (TOADO-740).
  const { data } = useQuery({ queryKey: ['inbox-counts', companyId], queryFn: () => api.getInboxCounts(companyId) });
  const { data: trends } = useQuery({ queryKey: ['dashboard', companyId, 'trends'], queryFn: () => api.dashboard.trends(companyId) });
  if (!data) return null;
  const figures: Array<[string, number, string, LucideIcon, Tone, 'assigned' | 'created' | 'mentioned']> = [
    ['Assigned to you', data.assigned, '/inbox/assigned', UserCheck, 'blue', 'assigned'],
    ['Created by you', data.created, '/inbox/created', PenLine, 'violet', 'created'],
    ['New mentions', data.mentioned, '/inbox/mentioned', AtSign, 'amber', 'mentioned'],
  ];
  return (
    <Card title="Your numbers" testId="dash-numbers" icon={Gauge} tone="cyan">
      <div className={styles.figures}>
        {figures.map(([label, value, to, Icon, tone, key]) => (
          <Link key={label} to={to} className={styles.figure} style={toneStyle(tone)}>
            <Icon size={15} className={styles.figureIcon} aria-hidden="true" />
            <span className={styles.figureValue}>{value}</span>
            <span className={styles.figureLabel}>{label}</span>
            {trends?.[key].daily && <Sparkline values={trends[key].daily} className={styles.figureSpark} />}
            {trends && <Trend {...trends[key]} />}
          </Link>
        ))}
      </div>
    </Card>
  );
}

/** How long a snooze lasts. */
const SNOOZE_DAYS = 7;

/**
 * Gone quiet (16 Oct): grouped under each project, each ticket as a block,
 * each with Archive and Snooze over its corner. A snoozed ticket stays off
 * this card for a week, in this browser.
 */
function GoneQuiet({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'gone-quiet'], queryFn: () => api.dashboard.goneQuiet(companyId) });
  const prefs = useDashboardPrefs();
  const { busy, error, run } = useTicketAction(companyId);
  if (!data) return null;
  const tickets = data.tickets.filter((ticket) => !prefs?.snoozedUntil(ticket.id));
  if (tickets.length === 0) return null;
  const projects: Array<{ project: DashboardTicket['project']; tickets: DashboardTicket[] }> = [];
  for (const ticket of tickets) {
    const group = projects.find((entry) => entry.project.id === ticket.project.id);
    if (group) group.tickets.push(ticket);
    else projects.push({ project: ticket.project, tickets: [ticket] });
  }
  return (
    <Card title="Gone quiet" testId="dash-gone-quiet" icon={Hourglass} tone="amber" size="full">
      <p className={styles.note}>Open, with no activity for {data.quietDays} days. Archive what is done with; snooze what can wait a week.</p>
      {error && <p role="alert" className={styles.actionError}>{error}</p>}
      {projects.map(({ project, tickets: theirs }) => (
        <div key={project.id} className={styles.projectGroup} data-testid="dash-quiet-project">
          <Link to={`/projects/${project.id}/board`} className={styles.projectHead}>
            <ProjectMark project={project} size={20} />{project.name}
            <span className={styles.rowMeta}>{theirs.length === 1 ? '1 ticket' : `${theirs.length} tickets`}</span>
          </Link>
          <div className={styles.blocks}>
            {theirs.map((ticket) => (
              <TicketBlock key={ticket.id} ticket={ticket} showProject={false}
                note={<><Duration since={ticket.since} prefix="Quiet " />{ticket.column ? ` · ${ticket.column.name}` : ''}</>}
                actions={<>
                  <BlockAction label="Archive" icon={Archive} busy={busy === ticket.id} onClick={() => void run(ticket.id, () => api.archiveTicket(ticket.id))} />
                  {prefs && <BlockAction label={`Snooze ${SNOOZE_DAYS} days`} icon={BellOff} onClick={() => prefs.snooze(ticket.id, SNOOZE_DAYS)} />}
                </>} />
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

/** Cards the person hid, as one link under Your numbers that brings them back (16 Oct). */
function HiddenCards() {
  const prefs = useDashboardPrefs();
  if (!prefs || prefs.hidden.size === 0) return null;
  return (
    <p className={styles.hiddenCards}>
      <EyeOff size={14} aria-hidden="true" className={styles.hiddenIcon} />
      <button type="button" className={styles.linkButton} onClick={prefs.showAll} data-testid="dash-hidden-cards">
        {prefs.hidden.size === 1 ? '1 card hidden, show it again' : `${prefs.hidden.size} cards hidden, show them again`}
      </button>
    </p>
  );
}
